import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";

const FX = { transformBox: "fill-box", transformOrigin: "center" };
const FXB = { transformBox: "fill-box", transformOrigin: "bottom" };

const WaveBg = () => (
  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 200" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 118 C 80 88, 150 138, 230 112 S 360 88, 400 118 L400 200 L0 200 Z" fill="#EFF6FF" />
    <path d="M0 152 C 90 126, 180 166, 260 146 S 372 128, 400 152 L400 200 L0 200 Z" fill="#DBEAFE" opacity="0.45" />
  </svg>
);

const FrontDeskArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <defs>
      <linearGradient id="fd-band" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
    </defs>
    <ellipse cx="150" cy="162" rx="92" ry="13" fill="#DBEAFE" opacity="0.55" />
    <circle cx="150" cy="118" r="66" fill="none" stroke="#93C5FD" strokeWidth="2"
      className="opacity-0 scale-90 transition-[opacity,transform] duration-500 ease-out group-hover:opacity-70 group-hover:scale-105 motion-reduce:transform-none" style={FX} />
    <g>
      <path d="M97 128 C 97 76, 203 76, 203 128" fill="none" stroke="url(#fd-band)" strokeWidth="12" strokeLinecap="round" />
      <rect x="84" y="116" width="26" height="44" rx="12" fill="#2563EB" />
      <rect x="190" y="116" width="26" height="44" rx="12" fill="#2563EB" />
      <path d="M203 150 C 203 168, 189 172, 176 172" fill="none" stroke="#1D4ED8" strokeWidth="7" strokeLinecap="round" />
      <circle cx="172" cy="172" r="7" fill="#1D4ED8" />
    </g>
    <g className="transition-transform duration-300 ease-out group-hover:translate-x-1.5 group-hover:-translate-y-1.5 motion-reduce:transform-none" style={FX}>
      <rect x="222" y="84" width="64" height="42" rx="14" fill="#FFFFFF" stroke="#DBEAFE" strokeWidth="2" />
      <path d="M238 126 l-8 15 l17 -11 z" fill="#FFFFFF" stroke="#DBEAFE" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="240" cy="105" r="4" fill="#60A5FA" />
      <circle cx="254" cy="105" r="4" fill="#60A5FA" />
      <circle cx="268" cy="105" r="4" fill="#60A5FA" />
    </g>
  </svg>
);

const SalesArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <ellipse cx="170" cy="162" rx="98" ry="13" fill="#DBEAFE" opacity="0.55" />
    <g className="transition-transform duration-300 ease-out group-hover:scale-y-[1.06] motion-reduce:transform-none" style={FXB}>
      <rect x="118" y="124" width="24" height="28" rx="6" fill="#93C5FD" />
      <rect x="152" y="106" width="24" height="46" rx="6" fill="#60A5FA" />
      <rect x="186" y="86" width="24" height="66" rx="6" fill="#3B82F6" />
      <rect x="220" y="62" width="24" height="90" rx="6" fill="#2563EB" />
    </g>
    <g className="transition-transform duration-300 ease-out group-hover:translate-x-1 group-hover:-translate-y-1.5 motion-reduce:transform-none" style={FX}>
      <path d="M116 112 C 150 104, 172 96, 196 76 S 234 52, 243 48" fill="none" stroke="#1D4ED8" strokeWidth="6" strokeLinecap="round" />
      <path d="M230 44 L248 44 L248 62" fill="none" stroke="#1D4ED8" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  </svg>
);

const OfficeArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <ellipse cx="160" cy="164" rx="88" ry="12" fill="#DBEAFE" opacity="0.55" />
    <g className="transition-transform duration-300 ease-out group-hover:-translate-y-1 group-hover:-rotate-[2deg] motion-reduce:transform-none" style={FX}>
      <circle cx="126" cy="46" r="7" fill="none" stroke="#1D4ED8" strokeWidth="5" />
      <circle cx="196" cy="46" r="7" fill="none" stroke="#1D4ED8" strokeWidth="5" />
      <rect x="96" y="52" width="130" height="106" rx="16" fill="#FFFFFF" stroke="#DBEAFE" strokeWidth="2" />
      <rect x="96" y="52" width="130" height="30" rx="16" fill="#2563EB" />
      <rect x="96" y="68" width="130" height="14" fill="#2563EB" />
      {[98, 118, 138].map((y) =>
        [110, 134, 158, 182].map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="16" height="12" rx="3"
            fill={x === 158 && y === 118 ? "#60A5FA" : "#DBEAFE"}
            className={x === 158 && y === 118 ? "transition-colors duration-300 group-hover:fill-[#1D4ED8]" : undefined} />
        ))
      )}
    </g>
  </svg>
);

const BillingArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <ellipse cx="160" cy="164" rx="86" ry="12" fill="#DBEAFE" opacity="0.55" />
    <g className="transition-transform duration-300 ease-out group-hover:-translate-y-1.5 motion-reduce:transform-none" style={FX}>
      <g transform="rotate(-8 167 110)">
        <rect x="112" y="46" width="110" height="128" rx="14" fill="#FFFFFF" stroke="#DBEAFE" strokeWidth="2" />
        <text x="140" y="88" fontSize="38" fontWeight="800" fill="#2563EB"
          className="transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transform-none" style={FX}>$</text>
        <rect x="128" y="100" width="78" height="8" rx="4" fill="#DBEAFE" />
        <rect x="128" y="118" width="58" height="8" rx="4" fill="#DBEAFE" />
        <rect x="128" y="136" width="70" height="8" rx="4" fill="#DBEAFE" />
      </g>
    </g>
  </svg>
);

const PeopleArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <ellipse cx="160" cy="162" rx="96" ry="13" fill="#DBEAFE" opacity="0.55" />
    <g className="transition-transform duration-300 ease-out group-hover:-translate-x-1.5 motion-reduce:transform-none" style={FX}>
      <circle cx="126" cy="94" r="17" fill="#1D4ED8" />
      <path d="M98 160 C 98 126, 154 126, 154 160 Z" fill="#3B82F6" />
    </g>
    <g className="transition-transform duration-300 ease-out group-hover:translate-x-1.5 motion-reduce:transform-none" style={FX}>
      <circle cx="194" cy="88" r="18" fill="#2563EB" />
      <path d="M164 160 C 164 122, 224 122, 224 160 Z" fill="#60A5FA" />
    </g>
  </svg>
);

const SupportArt = () => (
  <svg viewBox="0 0 320 190" className="w-full h-full" aria-hidden="true">
    <ellipse cx="160" cy="162" rx="84" ry="12" fill="#DBEAFE" opacity="0.55" />
    <g className="transition-transform duration-300 ease-out group-hover:rotate-[8deg] motion-reduce:transform-none" style={FX}>
      <circle cx="160" cy="102" r="46" fill="none" stroke="#2563EB" strokeWidth="22" />
      <circle cx="160" cy="102" r="46" fill="none" stroke="#FFFFFF" strokeWidth="22"
        strokeDasharray="36.1 36.1" strokeDashoffset="18" />
      <circle cx="160" cy="102" r="24" fill="#FFFFFF" stroke="#DBEAFE" strokeWidth="2" />
    </g>
  </svg>
);

const SERVICES = [
  { title: "Front Desk", desc: "Never miss a call, message, lead, or appointment.", Art: FrontDeskArt },
  { title: "Sales", desc: "Capture leads and move them through your pipeline.", Art: SalesArt },
  { title: "Office", desc: "Schedule, manage tasks and keep your operations running.", Art: OfficeArt },
  { title: "Billing", desc: "Send invoices, track payments and keep cash flow moving.", Art: BillingArt },
  { title: "People", desc: "Onboard, manage and support your team with ease.", Art: PeopleArt },
  { title: "Support", desc: "Resolve requests faster and delight your customers.", Art: SupportArt },
];

export const Operations = () => (
  <section id="solutions" data-testid="operations-section" className="scroll-mt-36 bg-white">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
      <Chapter
        number="01"
        label="One Nova, Everywhere"
        title="One Nova. Across Your Business."
        sub="Choose what you need. Nova connects the work across your business."
      >
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.085 * i} y={12}>
              <Link
                to="/request-demo"
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex flex-col h-full rounded-[22px] border border-[#E7EEF9] bg-white shadow-[0_6px_24px_rgb(7,26,56,0.05)] overflow-hidden transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_20px_48px_-12px_rgb(7,26,56,0.14)] hover:border-[#BFDBFE] motion-reduce:hover:transform-none"
              >
                <div className="relative h-44 sm:h-48 bg-[#F7FAFF] flex items-center justify-center overflow-hidden">
                  <WaveBg />
                  <div className="relative w-60 h-36 transition-[transform,filter] duration-300 ease-out group-hover:-translate-y-1 group-hover:brightness-[1.06] motion-reduce:transform-none">
                    <s.Art />
                  </div>
                </div>
                <div className="relative flex-1 p-6 sm:p-7 pb-7">
                  <h3 className="text-xl font-bold tracking-tight text-[#071A38]">{s.title}</h3>
                  <p className="mt-2 text-sm text-nova-slate leading-relaxed max-w-[85%]">{s.desc}</p>
                  <span
                    data-testid={`service-arrow-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className="absolute bottom-6 right-6 w-11 h-11 rounded-full border border-[#E2E8F0] bg-white flex items-center justify-center transition-colors duration-300 group-hover:bg-[#1D4ED8] group-hover:border-[#1D4ED8]"
                  >
                    <ArrowUpRight
                      size={17}
                      strokeWidth={2}
                      className="text-[#1D4ED8] transition-[color,transform] duration-300 ease-out group-hover:text-white group-hover:translate-x-[2px] group-hover:-translate-y-[2px] motion-reduce:transform-none"
                    />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 text-center">
          <p data-testid="operations-statement" className="inline-flex items-center gap-2.5 text-base text-nova-slate">
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
              <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" fill="#2563EB" />
            </svg>
            <span>
              Start with one. Add more when you need them.{" "}
              <span className="font-semibold text-[#1D4ED8]">Nova keeps everything connected.</span>
            </span>
          </p>
        </Reveal>
      </Chapter>
    </div>
  </section>
);
